import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card,
  PageHeader,
  GhostBtn,
  GoldBtn,
  OutlineBtn,
  SectionTitle,
  Field,
  Input,
  Textarea,
} from "../components/common/Ui";

const HomePromotional = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const mapRow = (r) => ({
    id: r.id,
    group: r.group_name || "",
    name: r.title,
    description: r.description || "",
    image: r.image_url,
  });

 const load = () => {
  setLoading(true);
  setLoadError("");
  apiFetch("/home/promo.php?admin=true")
    .then((res) => setCards((res.data || []).map(mapRow)))
    .catch((err) => setLoadError(err.message || "Failed to load cards."))
    .finally(() => setLoading(false));
};

  useEffect(load, []);

  const openEdit = (card) => {
    setForm(card);
    setNewFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(null);
    setNewFile(null);
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewFile(file);
    setForm((f) => ({ ...f, image: URL.createObjectURL(file) }));
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("id", form.id);
      fd.append("title", form.name);
      fd.append("description", form.description || "");
      if (newFile) fd.append("image", newFile);

      await apiFetch("/home/promo.php", { method: "POST", body: fd });
      closeForm();
      load();
    } catch (err) {
      alert(err.message || "Failed to save card.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6" style={{ background: T.bg, minHeight: "100vh" }}>
      <PageHeader
        title="Home Promotional"
        subtitle='Manage the "Crafted for Every Moment" cards on the homepage'
      />

      <Card className="p-4 sm:p-6">
        <SectionTitle>Promotional Cards</SectionTitle>

        {loading ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.textSec }}>Loading…</p>
        ) : loadError ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.danger }}>{loadError}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {cards.map((c) => (
              <div key={c.id} className="rounded-2xl overflow-hidden flex flex-col" style={{ border: `1px solid ${T.border}` }}>
                <div className="relative aspect-[4/3] flex items-center justify-center" style={{ background: T.bgSoft }}>
                  {c.image ? (
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[11px]" style={{ color: T.textSec }}>No photo</span>
                  )}
                </div>

                <div className="p-3.5 flex-1 flex flex-col gap-1">
                  {c.group && (
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: T.gold }}>{c.group}</p>
                  )}
                  <p className="text-[14px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>{c.name || "Untitled"}</p>
                  <p className="text-[11px] leading-relaxed line-clamp-3" style={{ color: T.textSec }}>{c.description}</p>
                </div>

                <div className="p-2.5" style={{ borderTop: `1px solid ${T.border}` }}>
                  <GhostBtn onClick={() => openEdit(c)}>Edit</GhostBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Edit form modal — photo / name / description only ── */}
      {formOpen && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={closeForm}>
          <div className="rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" style={{ background: T.card }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-[15px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Edit Card</h3>
              <button onClick={closeForm} className="text-[13px] font-medium" style={{ color: T.textSec }}>✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <Field label="Photo">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
                    {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <span className="text-[9px] text-center px-1" style={{ color: T.textSec }}>No photo</span>}
                  </div>
                  <label className="flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-4 text-[12px] font-semibold cursor-pointer transition-all flex-1" style={{ border: `1.5px dashed ${T.gold}`, color: T.gold }}>
                    Choose from gallery
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoPick} />
                  </label>
                </div>
              </Field>

              <Field label="Name" required>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Photobooks" />
              </Field>

              <Field label="Description">
                <Textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short description shown on the card" />
              </Field>
            </div>

            <div className="flex items-center gap-2.5 p-5 flex-shrink-0" style={{ borderTop: `1px solid ${T.border}` }}>
              <OutlineBtn onClick={closeForm}>Cancel</OutlineBtn>
              <GoldBtn onClick={handleSave}>{saving ? "Saving…" : "Save Changes"}</GoldBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePromotional;